import { useState, useCallback } from 'react';
import { AppliedFilter, FilterHistory } from './types';

type HistoryState = { entries: FilterHistory[]; index: number };

const emptyHistory = (): HistoryState => ({
    entries: [{ filters: [], timestamp: Date.now() }],
    index: 0,
});

/**
 * Histórico das buscas do painel, com desfazer/refazer.
 *
 * Lista e posição num estado SÓ: com dois `useState`, o `push` atualizava o índice duas vezes (uma
 * dentro do updater da lista, outra fora) e ele saía da lista. Cada operação agora lê e escreve o
 * par no mesmo updater, então duas chamadas no mesmo tick também caem em ordem.
 */
export const useFilterHistory = () => {
    const [state, setState] = useState<HistoryState>(emptyHistory);

    const push = useCallback((filters: AppliedFilter[]) => {
        setState(prev => {
            const entries = [
                ...prev.entries.slice(0, prev.index + 1),
                { filters: [...filters], timestamp: Date.now() },
            ];
            return { entries, index: entries.length - 1 };
        });
    }, []);

    const undo = useCallback(() => {
        setState(prev => ({ ...prev, index: Math.max(0, prev.index - 1) }));
    }, []);

    const redo = useCallback(() => {
        setState(prev => ({ ...prev, index: Math.min(prev.entries.length - 1, prev.index + 1) }));
    }, []);

    const reset = useCallback(() => {
        setState(emptyHistory());
    }, []);

    return {
        current: state.entries[state.index]?.filters || [],
        push,
        undo,
        redo,
        reset,
        canUndo: state.index > 0,
        canRedo: state.index < state.entries.length - 1,
        historyLength: state.entries.length,
    };
};
